import { useState } from "react";
import { motion } from "framer-motion";
import { FileDown } from "lucide-react";
import { Seo } from "@/components/Seo";
import { ProductCard } from "@/components/ProductCard";
import { PRODUCTS, CATEGORIES, CATEGORY_LABEL } from "@/data/products";
import { API_ROOT } from "@/lib/api";

const CATEGORY_INTROS = {
  women: "Women's denim jeans manufactured to your fits, washes and size grading — from skinny to wide-leg programs.",
  men: "Men's denim jeans in core and fashion fits, produced to buyer specifications at factory-direct terms.",
  kids: "Kids' denim built for durability and comfort, with adjustable waists and buyer-specified trims.",
  oem: "Custom and OEM styles developed from your tech pack — your fits, your washes, your labels.",
};

export default function Catalogue({ category = null }) {
  const [filter, setFilter] = useState(category || "all");
  const items = filter === "all" ? PRODUCTS : PRODUCTS.filter((p) => p.category === filter);
  const isCategoryPage = Boolean(category);
  const title = isCategoryPage ? CATEGORY_LABEL[category] : "Sample Catalogue";
  const seoTitle = isCategoryPage
    ? `${CATEGORY_LABEL[category]} Manufacturer — Shineni India, Faridabad`
    : "Sample Denim Catalogue — Shineni India";

  return (
    <div data-testid="catalogue-page">
      <Seo
        title={seoTitle}
        description={`${CATEGORY_INTROS[filter] || "Sample denim styles across women's, men's, kids' and OEM ranges."} All images are representative samples.`}
      />

      <section className="border-b border-border bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gold">Shineni India · Faridabad</p>
          <h1 className="mt-3 font-display text-4xl font-semibold text-indigo sm:text-5xl">{title}</h1>
          <p className="mt-4 max-w-2xl text-base text-foreground/70">
            {(isCategoryPage ? CATEGORY_INTROS[category] : "Browse representative sample styles across our women's, men's, kids' and OEM ranges. ")}
            Every order is produced to your tech pack.{" "}
            <span className="font-medium text-indigo">All images are sample imagery, not photos of shipped production.</span>
          </p>
          <a
            href={`${API_ROOT}/api/catalogue/line-sheet.pdf`}
            data-testid="catalogue-linesheet-download"
            className="mt-6 inline-flex items-center gap-2 rounded-sm border border-indigo px-5 py-2.5 text-sm font-semibold text-indigo transition-colors duration-200 hover:bg-indigo hover:text-white"
          >
            <FileDown className="h-4 w-4" /> Download line sheet (PDF)
          </a>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-16">
        {!isCategoryPage && (
          <div className="mb-10 flex flex-wrap gap-2" role="tablist" aria-label="Filter catalogue">
            {[{ id: "all", label: "All Ranges" }, ...CATEGORIES].map((c) => (
              <button
                key={c.id}
                role="tab"
                aria-selected={filter === c.id}
                data-testid={`catalogue-filter-${c.id}`}
                onClick={() => setFilter(c.id)}
                className={`rounded-sm border px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                  filter === c.id
                    ? "border-indigo bg-indigo text-white"
                    : "border-border bg-white text-foreground/70 hover:border-indigo hover:text-indigo"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        )}

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="catalogue-grid">
          {items.map((p) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
              <ProductCard product={p} />
            </motion.div>
          ))}
        </div>

        <div className="mt-14 rounded-sm border border-gold/40 bg-gold/10 p-8">
          <h2 className="font-display text-xl font-semibold text-indigo">Don't see your style?</h2>
          <p className="mt-2 max-w-2xl text-sm text-foreground/70">
            These are sample styles only. Share your tech pack or reference samples and we will
            develop your exact fit, wash and finish. Minimums and pricing are quoted per program.
          </p>
        </div>
      </section>
    </div>
  );
}
